(() => {
  'use strict';

  const SECTION_SELECTOR = '[data-section-type="bcu-cleaning-home"], [data-section-type="bcu-service-configurator"]';
  const mobileQuery = window.matchMedia('(max-width: 749px)');

  const directHeading = (step) => Array.from(step.children).find((child) => (
    child.matches?.('.bcu-service-step__heading, legend')
  ));

  const revealStepHeading = (step) => {
    const heading = directHeading(step);
    if (!heading || !mobileQuery.matches) return;
    window.requestAnimationFrame(() => {
      heading.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'auto' });
    });
  };

  const summaryValueFor = (section, step) => {
    const key = step.dataset.bcuStep || '';
    const output = Array.from(section.querySelectorAll('[data-bcu-summary]'))
      .find((node) => node.dataset.bcuSummary === key);
    const value = output?.textContent?.replace(/\s+/g, ' ').trim() || '';
    if (value && value !== '–') return value;
    return directHeading(step)?.querySelector('small')?.textContent?.replace(/\s+/g, ' ').trim() || '';
  };

  const updateStepSummary = (section, step) => {
    const summary = step.querySelector(':scope > .bcu-service-step__heading .bcu-mobile-step__summary, :scope > legend .bcu-mobile-step__summary');
    if (!summary) return;
    const value = summaryValueFor(section, step);
    if (summary.textContent !== value) summary.textContent = value;
    if (summary.hidden === Boolean(value)) summary.hidden = !value;
  };

  const setStepOpen = (section, step, open, moveFocus = false) => {
    if (!mobileQuery.matches) open = true;
    if (mobileQuery.matches && step.dataset.bcuMobilePersistentOpen === 'true') open = true;
    /* Persistent panels (currently insurance) coexist with the active standard
       step and must not collapse Step 1 while the initial state is built. */
    if (open && mobileQuery.matches && step.dataset.bcuMobilePersistentOpen !== 'true') {
      section.querySelectorAll('[data-bcu-step].is-bcu-mobile-open').forEach((otherStep) => {
        if (otherStep === step || otherStep.dataset.bcuMobilePersistentOpen === 'true') return;
        otherStep.classList.remove('is-bcu-mobile-open');
        otherStep.classList.add('is-bcu-mobile-collapsed');
        otherStep.querySelector('[data-bcu-mobile-step-toggle]')?.setAttribute('aria-expanded', 'false');
        updateStepSummary(section, otherStep);
      });
    }

    step.classList.toggle('is-bcu-mobile-open', open);
    step.classList.toggle('is-bcu-mobile-collapsed', !open && mobileQuery.matches);
    const toggle = step.querySelector('[data-bcu-mobile-step-toggle]');
    toggle?.setAttribute('aria-expanded', String(open));
    updateStepSummary(section, step);

    if (moveFocus && open) {
      window.requestAnimationFrame(() => {
        const target = step.querySelector('input:not([type="hidden"]), select, textarea, button:not([data-bcu-mobile-step-toggle])');
        target?.focus({ preventScroll: true });
      });
    }
  };

  const enhanceStep = (section, step, index) => {
    if (step.dataset.bcuMobileAccordion === 'true') return;
    const heading = directHeading(step);
    if (!heading) return;
    const content = step.querySelector(':scope > [data-bcu-step-content]') || Array.from(step.children).find((child) => child !== heading);
    const title = heading.querySelector('strong')?.textContent?.replace(/\s+/g, ' ').trim() || `Schritt ${index + 1}`;
    const copy = Array.from(heading.children).find((child) => !child.matches('.bcu-service-step__number, .bcu-service-step__required'));
    const isPersistentInsurance = /^versicherung\b/i.test(title);

    step.dataset.bcuMobileAccordion = 'true';
    if (isPersistentInsurance) step.dataset.bcuMobilePersistentOpen = 'true';
    if (content && !content.id) content.id = `BcuMobileStep-${section.dataset.sectionId || 'service'}-${index + 1}`;

    const summary = document.createElement('span');
    summary.className = 'bcu-mobile-step__summary';
    summary.setAttribute('aria-live', 'polite');
    (copy || heading).appendChild(summary);

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'bcu-mobile-step__toggle';
    toggle.dataset.bcuMobileStepToggle = '';
    toggle.setAttribute('aria-label', `${title} ein- oder ausklappen`);
    toggle.setAttribute('aria-expanded', 'false');
    if (content?.id) toggle.setAttribute('aria-controls', content.id);
    toggle.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m7 10 5 5 5-5"/></svg>';
    heading.appendChild(toggle);
    toggle.addEventListener('click', () => {
      if (!mobileQuery.matches || isPersistentInsurance) return;
      const opening = !step.classList.contains('is-bcu-mobile-open');
      setStepOpen(section, step, opening);
      if (opening) revealStepHeading(step);
    });
    heading.addEventListener('click', (event) => {
      if (!mobileQuery.matches || isPersistentInsurance || event.target.closest('[data-bcu-mobile-step-toggle]')) return;
      const opening = !step.classList.contains('is-bcu-mobile-open');
      setStepOpen(section, step, opening);
      if (opening) revealStepHeading(step);
    });
    updateStepSummary(section, step);
  };

  const syncResponsiveState = (section) => {
    const steps = Array.from(section.querySelectorAll('[data-bcu-step]'));
    const included = section.querySelector('[data-bcu-mobile-included]');
    const includedToggle = included?.querySelector('[data-bcu-mobile-included-toggle]');
    const includedContent = included?.querySelector('[data-bcu-mobile-included-content]');

    steps.forEach((step, index) => {
      const toggle = step.querySelector('[data-bcu-mobile-step-toggle]');
      const persistentOpen = step.dataset.bcuMobilePersistentOpen === 'true';
      if (toggle) toggle.hidden = !mobileQuery.matches || persistentOpen;
      if (!mobileQuery.matches) {
        setStepOpen(section, step, true);
      } else if (persistentOpen) {
        setStepOpen(section, step, true);
      } else if (section.dataset.bcuMobileAccordionActivated !== 'true') {
        /* Step 1 starts open on mobile; the remaining standard steps stay collapsed. */
        setStepOpen(section, step, index === 0);
      } else {
        updateStepSummary(section, step);
      }
    });

    if (mobileQuery.matches) {
      section.dataset.bcuMobileAccordionActivated = 'true';
      if (included && included.dataset.bcuMobileInitialState !== 'set') {
        included.dataset.bcuMobileInitialState = 'set';
        included.classList.remove('is-bcu-mobile-open');
        includedToggle?.setAttribute('aria-expanded', 'false');
      }
      if (includedToggle) includedToggle.tabIndex = 0;
      if (includedContent) includedContent.hidden = !included?.classList.contains('is-bcu-mobile-open');
    } else {
      delete section.dataset.bcuMobileAccordionActivated;
      if (includedToggle) {
        includedToggle.tabIndex = -1;
        includedToggle.setAttribute('aria-expanded', 'true');
      }
      if (includedContent) includedContent.hidden = false;
    }
  };

  const initialise = (section) => {
    if (section.dataset.bcuMobileServiceInitialised === 'true') return;
    section.dataset.bcuMobileServiceInitialised = 'true';

    Array.from(section.querySelectorAll('[data-bcu-step]')).forEach((step, index) => enhanceStep(section, step, index));

    const included = section.querySelector('[data-bcu-mobile-included]');
    const includedToggle = included?.querySelector('[data-bcu-mobile-included-toggle]');
    const includedContent = included?.querySelector('[data-bcu-mobile-included-content]');
    includedToggle?.addEventListener('click', () => {
      if (!mobileQuery.matches || !includedContent) return;
      const open = !included.classList.contains('is-bcu-mobile-open');
      included.classList.toggle('is-bcu-mobile-open', open);
      includedToggle.setAttribute('aria-expanded', String(open));
      includedContent.hidden = !open;
    });

    let frame = 0;
    const observer = new MutationObserver((mutations) => {
      const invalidStep = mutations
        .map((mutation) => mutation.target?.closest?.('[data-bcu-step]'))
        .find((step) => step?.classList.contains('has-validation-error'));
      if (invalidStep && mobileQuery.matches) setStepOpen(section, invalidStep, true);
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        section.querySelectorAll('[data-bcu-step]').forEach((step) => updateStepSummary(section, step));
      });
    });
    observer.observe(section, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class', 'hidden'],
    });

    syncResponsiveState(section);
    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', () => syncResponsiveState(section));
    } else if (typeof mobileQuery.addListener === 'function') {
      mobileQuery.addListener(() => syncResponsiveState(section));
    }
  };

  const initialiseAll = (root = document) => root.querySelectorAll(SECTION_SELECTOR).forEach(initialise);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialiseAll(), { once: true });
  } else {
    initialiseAll();
  }
  document.addEventListener('shopify:section:load', (event) => initialiseAll(event.target));
})();
